import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Link, Navigate, useLocation, useSearchParams } from 'react-router-dom'
import { api, ApiError, useMe } from '../../lib'
import { ErrorBox } from '../../app/ui'
import { landingFor } from '../../app/Shell'
import { current } from '../../app/paths'
import { SkyCard } from '../../app/SkyCard'

// Same-site paths only, starting with exactly one '/'. The server applies the same check (pr/login/service.py
// safe_next): no other host ('//x', '/\x'), no scheme, no control or non-ASCII characters, and none of those
// percent-encoded either ('/%2F%2Fevil.com', '/%5Cevil.com', '/%09/evil.com'). The query string is kept.
const BAD_CHARS = /[\\\u0000-\u001f\u007f-\uffff]/

function safeNext(v: string | null | undefined): string | null {
  if (!v || v.length > 512 || !v.startsWith('/') || v.startsWith('//') || BAD_CHARS.test(v)) return null
  let decoded = v
  for (let i = 0; i < 3; i++) {
    const before = decoded
    decoded = unquote(decoded)
    if (decoded.startsWith('//') || BAD_CHARS.test(decoded)) return null
    if (decoded === before) return v
  }
  return null // still encoded after three passes: nothing honest needs that
}

// Like Python's unquote: decode each run of %XX (as UTF-8 when it is valid), leave anything else as it is.
function unquote(v: string): string {
  return v.replace(/(%[0-9a-fA-F]{2})+/g, (run) => {
    try {
      return decodeURIComponent(run)
    } catch {
      return '\ufffd' // not UTF-8: Python decodes it to U+FFFD too, which is refused
    }
  })
}

// The ernd.io "Start earning" link's tags, passed on to the server's sign-in so it can keep the first touch.
// utm_content and ref carry the outbound prospect page's slug (migrations/991_acquisition_ref.sql), and invite a
// cardholder's invite code (migrations/994_card_invites.sql).
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'ref', 'invite'] as const

function utmFrom(params: URLSearchParams): Record<string, string> {
  const out: Record<string, string> = {}
  for (const k of UTM_KEYS) {
    const v = params.get(k)
    if (v) out[k] = v.slice(0, 200)
  }
  return out
}

// ernd.io's calculator hands over the address someone typed, so they type it once (CHANGE-052). It comes in the URL
// fragment because browsers never send a fragment to a server, so it can't reach a log or a Referer. Never a query string.
// It only fills the field: nothing is sent until they press the button, so a link can't mail anyone.
const HANDED_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

function handedEmail(): string {
  const value = (new URLSearchParams(window.location.hash.slice(1)).get('email') ?? '').trim()
  return value.length <= 254 && HANDED_EMAIL.test(value) ? value : ''
}

// Google refuses sign-in from a browser inside another app (LinkedIn, Facebook, Instagram, TikTok, an Android webview):
// "disallowed_useragent", a dead end. Those browsers get the email link, which opens wherever the mail does (CHANGE-063).
const IN_APP = /\b(LinkedInApp|FBAN|FBAV|FB_IAB|Instagram|Barcelona|Threads|Twitter|Snapchat|Pinterest|BytedanceWebview|MicroMessenger)\b|; wv\)/i
const inAppBrowser = () => IN_APP.test(navigator.userAgent)

/** /partners: the short link for ernd.io and sales mails. Keeps any utm tags. */
export function PartnersShortLink() {
  const { search } = useLocation()
  const params = new URLSearchParams(search)
  params.set('as', 'partner')
  return <Navigate to={`/login?${params.toString()}`} replace />
}

/** The other version of this page, keeping the utm tags, and `next` only when it is not tied to the side left. */
function switchLink(params: URLSearchParams, toPartner: boolean): string {
  const q = new URLSearchParams(utmFrom(params))
  const next = safeNext(params.get('next'))
  if (next && next.startsWith('/partner') === toPartner) q.set('next', next)
  if (toPartner) q.set('as', 'partner')
  const s = q.toString()
  return s ? `/login?${s}` : '/login'
}

export function LoginPage() {
  const { me, isLoading, failed } = useMe()
  // Once the session check has failed they get the form and keep it (CHANGE-063): each retry is a new load, and sign-in
  // doesn't need the check.
  const checked = useRef(false)
  if (failed) checked.current = true
  const [params] = useSearchParams()
  const [handed] = useState(handedEmail)
  // Out of the address bar, history and any link copied from it, the moment it's read.
  useEffect(() => {
    if (new URLSearchParams(window.location.hash.slice(1)).has('email')) {
      window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search)
    }
  }, [])
  const cardMode = !!me?.features?.card_only_app
  const asPartner = params.get('as') === 'partner' && (!cardMode || me?.is_staff)
  const given = safeNext(params.get('next'))
  // The partner version sends people to the partner side, unless a deep link asked for a page (CHANGE-038).
  const givenRoot = given ? (current(given) as string).split('?')[0].replace(/\/+$/, '') || '/' : ''
  const next = asPartner && (!given || givenRoot === '/') ? '/partner' : given
  // Already signed in, so no sign-in will keep this link's tags: keep them first. A failure never stops them going on.
  const signedIn = !!me?.user
  const tagged = Object.keys(utmFrom(params)).length > 0
  const [kept, setKept] = useState(false)
  useEffect(() => {
    if (!signedIn || !tagged) return
    api('POST', '/auth/touch', { ...(next ? { next } : {}), ...utmFrom(params) }).catch(() => {}).finally(() => setKept(true))
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once, when the session check answers
  }, [signedIn])
  if (me?.user && tagged && !kept) return <div className="e-signin e-stage" data-testid="accounts-login-checking" aria-busy="true" />
  if (me?.user) return <Navigate to={landingFor(me, next)} replace />
  // Until the session check answers, show the empty stage, not the sign-in buttons: someone already signed in (an
  // ernd.io "Request early access" link) goes straight on without a flash of Google sign-in.
  if (isLoading && !checked.current) return <div className="e-signin e-stage" data-testid="accounts-login-checking" aria-busy="true" />
  return (
    <div className="e-signin e-stage" data-testid="accounts-login" data-side={asPartner ? 'partner' : 'company'}>
      <h1 className="e-title">{asPartner ? 'Sign in as a partner' : 'Sign in to ernd'}</h1>
      <p className="e-lead">{asPartner ? 'For companies that bill AI startups.' : inAppBrowser() ? 'Use a work email.' : (me?.features?.microsoft_login ? 'Use a work email, Google or Microsoft account.' : 'Use a work email or a Google work account.')}</p>
      <SignInForm key={next} next={next} refused={params.get('error') === 'work_email'} microsoft={!!me?.features?.microsoft_login} email={handed} />
      {cardMode ? null : asPartner ? (
        <p className="e-fine e-signin-switch">
          Not a partner? <Link to={switchLink(params, false)} data-testid="accounts-as-company">Sign in here</Link>
        </p>
      ) : (
        <>
          <p className="e-fine">Free to join for US companies.</p>
          <p className="e-fine e-signin-switch">
            Are you a partner? <Link to={switchLink(params, true)} data-testid="accounts-as-partner">Sign in as a partner</Link>
          </p>
        </>
      )}
      <div className="e-signin-card" aria-hidden="true">
        <SkyCard />
      </div>
    </div>
  )
}

/** Google and email sign-in, returning to `next`. Also used inline on pages a signed-out visitor lands on.
 * `refused`: Google sent back a personal account (CHANGE-027), so open with the same line the email path shows.
 * `email`: an address to start the field with. */
export function SignInForm({ next, refused, microsoft, email: startEmail = '' }: { next?: string | null; refused?: boolean; microsoft?: boolean; email?: string }) {
  next = safeNext(next)
  const [params] = useSearchParams()
  const utm = utmFrom(params)
  const googleQuery = new URLSearchParams({ ...(next ? { next } : {}), ...utm }).toString()
  const [email, setEmail] = useState(startEmail)
  const [sent, setSent] = useState<string | null>(null)
  const [minutes, setMinutes] = useState<number | null>(null)
  const [again, setAgain] = useState(false)
  const embedded = inAppBrowser()
  const field = useRef<HTMLInputElement>(null)
  // While the link is out, keep asking whether this browser has signed in, so the tab they left moves on by itself
  // once the link is opened in another tab (CHANGE-053). /login sends a signed-in visitor on to `next`.
  useMe(sent ? 3000 : undefined)
  const failed = params.get('error')
  const [error, setError] = useState<unknown>(
    refused ? new Error("That account is personal. Sign in with your company's account, or enter your company email below.")
      : failed === 'microsoft_unverified' ? new Error('Microsoft could not confirm that email. Enter it below for a sign-in link.')
        : failed === 'microsoft' ? new Error('Microsoft sign-in did not finish. Try again, or enter your email below.')
          : null)
  const [busy, setBusy] = useState(false)

  async function submit(e: FormEvent) {
    e.preventDefault()
    await start(email)
  }

  async function start(address: string) {
    setBusy(true)
    setError(null)
    setAgain(false)
    try {
      const r = await api<{ sent: boolean; minutes?: number }>('POST', '/auth/email/start', { email: address, ...(next ? { next } : {}), ...utm })
      setMinutes(r?.minutes ?? null)
      setAgain(!!sent)
      setSent(address)
    } catch (err) {
      setError(err)
      // A personal address: select it, so typing the work one replaces it (CHANGE-053)
      if (err instanceof ApiError && err.code === 'work_email') requestAnimationFrame(() => field.current?.select())
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="e-signin-form">
      {embedded ? null : (
        <a className="e-btn e-btn-primary e-btn-lg e-btn-block" href={googleQuery ? `/auth/google/start?${googleQuery}` : '/auth/google/start'} data-testid="accounts-google">
          <GoogleG />
          Continue with Google
        </a>
      )}
      {microsoft && !embedded ? (
        <a className="e-btn e-btn-lg e-btn-block" href={googleQuery ? `/auth/microsoft/start?${googleQuery}` : '/auth/microsoft/start'} data-testid="accounts-microsoft">
          <MicrosoftMark />
          Continue with Microsoft
        </a>
      ) : null}
      {sent ? (
        <div className="e-signin-sent" role="status" data-testid="accounts-link-sent">
          <p>ernd sent a sign-in link to <strong>{sent}</strong>.</p>
          <p className="e-fine" data-testid="accounts-link-hint">
            {minutes ? `It works for ${minutes} minutes. ` : ''}If it isn't in your inbox, check spam.
          </p>
          {error ? <ErrorBox error={error} /> : again ? <p className="e-fine" data-testid="accounts-link-again">Sent again.</p> : null}
          <button type="button" className="e-btn e-btn-lg e-btn-block" disabled={busy} onClick={() => start(sent)} data-testid="accounts-send-again">Send it again</button>
          <button type="button" className="e-btn-text" onClick={() => { setSent(null); setError(null); setAgain(false) }} data-testid="accounts-different-email">Use a different email</button>
        </div>
      ) : (
        <>
          {embedded ? null : <div className="e-or">or</div>}
          <form className="e-signin-email" onSubmit={submit}>
            <label className="e-field">
              Work email
              <input
                ref={field}
                className="e-input e-input-lg"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="accounts-email"
              />
            </label>
            {error ? <ErrorBox error={error} /> : null}
            <button className="e-btn e-btn-lg e-btn-block" type="submit" disabled={busy} data-testid="accounts-send-link">
              Email me a sign-in link
            </button>
          </form>
        </>
      )}
    </div>
  )
}

function MicrosoftMark() {
  return (
    <svg className="e-google" viewBox="0 0 23 23" aria-hidden="true">
      <path fill="#f25022" d="M1 1h10v10H1z" />
      <path fill="#7fba00" d="M12 1h10v10H12z" />
      <path fill="#00a4ef" d="M1 12h10v10H1z" />
      <path fill="#ffb900" d="M12 12h10v10H12z" />
    </svg>
  )
}

function GoogleG() {
  return (
    <svg className="e-google" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
    </svg>
  )
}
